import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, Briefcase, PlusCircle, Users, Building2, Radio, Heart } from 'lucide-react';
import { useI18n } from '../i18n';
import { CATEGORIES, JOBS, PROFESSIONALS, COMPANIES, NEWS, TRAINING, EVENTS, STATS, sortJobs } from '../data/demo';
import Navbar from '../components/Navbar';
import { HeroVideo } from '../components/HeroVideo';
import Footer from '../components/Footer';
import SectionHeader from '../components/SectionHeader';
import { JobCard, ProfessionalCard, CompanyCard, NewsCard, TrainingCard, EventCard } from '../components/Cards';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tween a number toward its target so live changes visibly count up/down.
const AnimatedNumber = ({ value, testId }) => {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef();
  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    const start = performance.now();
    const dur = 700;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);
  return <span data-testid={testId}>{display}</span>;
};

const LiveStats = () => {
  const [stats, setStats] = useState(null);
  const [bump, setBump] = useState(false);
  const prevRef = useRef(null);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await fetch(`${API}/stats/public`);
        const d = await r.json();
        if (!active) return;
        if (prevRef.current && JSON.stringify(prevRef.current) !== JSON.stringify(d)) { setBump(true); setTimeout(() => active && setBump(false), 700); }
        prevRef.current = d;
        setStats(d);
      } catch { /* keep last known */ }
    };
    load();
    const id = setInterval(load, 8000);
    return () => { active = false; clearInterval(id); };
  }, []);
  const items = [
    { key: 'professionals', label: 'Profesionales', to: '/profesionales', Icon: Users },
    { key: 'companies', label: 'Empresas', to: '/empresas', Icon: Building2 },
    { key: 'jobs', label: 'Ofertas activas', to: '/ofertas', Icon: Briefcase },
  ];
  return (
    <section className="border-b border-[#2A2F3D] bg-[#0B0C0E]" data-testid="live-stats">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-3 gap-3 sm:gap-6">
          {items.map(({ key, label, to, Icon }) => (
            <Link key={key} to={to} data-testid={`live-stat-${key}`} className={`group flex items-center gap-3 justify-center sm:justify-start rounded-lg px-2 py-1 transition-transform ${bump ? 'scale-[1.03]' : ''}`}>
              <Icon size={18} className="text-amber-500 shrink-0" />
              <span className="min-w-0">
                <span className="block font-mono text-2xl sm:text-3xl font-bold text-white leading-none tabular-nums">
                  <AnimatedNumber value={stats?.[key] ?? 0} testId={`live-stat-${key}-value`} />
                </span>
                <span className="block text-[10px] sm:text-xs font-mono uppercase tracking-wider text-slate-500 mt-1 group-hover:text-amber-500 transition-colors">{label}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const DonationHomeCTA = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-4" data-testid="home-donation-cta">
    <div className="relative overflow-hidden sc-card p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between" style={{ background: 'radial-gradient(ellipse at top left, rgba(245,158,11,0.10), transparent 55%), #121418' }}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-2"><Heart size={16} className="text-amber-500" /><span className="sc-mono-eyebrow text-amber-500">Apoya el proyecto</span></div>
        <h2 className="text-lg md:text-xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Stage Crew es gratis. Y queremos que siga siendo así.</h2>
        <p className="text-sm text-slate-400 leading-relaxed max-w-xl">Mantener la plataforma tiene costes. Si te resulta útil, una pequeña aportación voluntaria ayuda a que siga creciendo. Sin presión: puedes seguir usándola gratis.</p>
      </div>
      <Link to="/donaciones" data-testid="home-donation-btn" className="shrink-0 inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors">
        <Heart size={16} /> Hacer una donación
      </Link>
    </div>
  </section>
);


const HeroSearch = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [loc, setLoc] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (loc) params.set('loc', loc);
    navigate(`/ofertas?${params.toString()}`);
  };
  return (
    <form onSubmit={submit} className="relative p-2 bg-[#121418] border border-[#2A2F3D] rounded-2xl shadow-2xl flex flex-col md:flex-row gap-2" data-testid="hero-search-form">
      <div className="flex items-center gap-3 px-4 flex-1">
        <Search size={18} className="text-slate-500 shrink-0" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('hero.searchPlaceholder')}
          aria-label={t('hero.searchWhat')}
          data-testid="hero-search-input"
          className="bg-transparent text-white placeholder:text-slate-500 border-0 outline-none py-3 flex-1 min-w-0"
        />
      </div>
      <div className="flex items-center gap-3 px-4 md:border-l border-[#2A2F3D] md:w-64">
        <MapPin size={18} className="text-slate-500 shrink-0" />
        <input
          type="text"
          value={loc}
          onChange={(e) => setLoc(e.target.value)}
          placeholder={t('hero.locationPlaceholder')}
          aria-label={t('hero.locationPlaceholder')}
          data-testid="hero-location-input"
          className="bg-transparent text-white placeholder:text-slate-500 border-0 outline-none py-3 w-full"
        />
      </div>
      <button
        type="submit"
        data-testid="hero-search-submit"
        className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-8 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 shrink-0"
      >
        {t('hero.searchButton')}
      </button>
    </form>
  );
};

const CategoryGrid = () => {
  const { t } = useI18n();
  return (
    <section className="py-16 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeader
        eyebrow="[ 01 ] Especialidades"
        title={t('section.categories')}
        subtitle={t('section.categories.sub')}
        testId="categories-header"
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {CATEGORIES.map((cat) => (
          <Link
            key={cat.id}
            to={`/ofertas?cat=${cat.id}`}
            data-testid={`category-card-${cat.id}`}
            className="group sc-card p-5 flex flex-col items-start gap-3 min-h-[110px]"
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.accent, boxShadow: `0 0 12px ${cat.accent}` }} />
            <span className="text-sm font-medium text-slate-200 group-hover:text-amber-500 transition-colors leading-tight">{cat.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
};

const StatsSection = () => {
  const { t } = useI18n();
  return (
    <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeader
        eyebrow="[ 07 ] La realidad del sector"
        title={t('section.stats')}
        subtitle={t('section.stats.sub')}
        testId="stats-header"
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATS.map((s) => (
          <div key={s.label} className="sc-card p-6 relative" data-testid={`stat-${s.label}`}>
            <div className="absolute top-3 right-3">
              <span className="text-[10px] font-mono uppercase tracking-widest text-amber-500 border border-amber-500/30 px-1.5 py-0.5 rounded bg-amber-500/5">DEMO</span>
            </div>
            <div className="font-mono text-3xl md:text-4xl font-bold text-white mb-1">{s.value}</div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-1">{s.label}</div>
            <div className="text-[11px] text-slate-500">{s.hint}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 font-mono mt-4 text-center">{t('section.stats.demoNote')}</p>
    </section>
  );
};

const EmployerCTA = () => {
  const { t } = useI18n();
  return (
    <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden sc-card p-10 md:p-16 text-center" style={{ background: 'radial-gradient(ellipse at top, rgba(245,158,11,0.14), transparent 60%), #121418' }}>
        <div className="sc-mono-eyebrow text-amber-500 mb-4">Para empresas y productoras</div>
        <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('cta.employer.title')}</h2>
        <p className="text-slate-400 max-w-2xl mx-auto mb-8 text-base md:text-lg">{t('cta.employer.text')}</p>
        <Link
          to="/publicar-oferta"
          data-testid="employer-cta-btn"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-8 py-3.5 rounded-xl transition-colors"
        >
          {t('cta.employer.button')} →
        </Link>
        <div className="text-xs text-slate-500 font-mono mt-4 uppercase tracking-wider">{t('cta.employer.note')}</div>
      </div>
    </section>
  );
};

export default function HomePage() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <LiveStats />

      {/* Hero — two clear CTAs first, search below */}
      <section className="relative overflow-hidden pt-12 pb-14 md:pt-20 md:pb-20" data-testid="hero-section">
        <HeroVideo />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="sc-mono-eyebrow text-amber-500 mb-5">
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Comunidad profesional · Catalunya / España
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight max-w-4xl mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Bienvenido/a a Stage Crew
          </h1>
          <p className="text-base md:text-lg text-slate-400 max-w-2xl mb-8 leading-relaxed">
            La casa de quienes hacen posible el directo. Técnicos, profesionales y empresas de las artes escénicas, juntos en un mismo sitio: encuentra trabajo, publica el tuyo y siéntete parte de la comunidad entre bambalinas.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="hero-ctas">
            <Link
              to="/ofertas"
              data-testid="cta-view-jobs"
              className="group relative sc-card p-6 md:p-8 flex flex-col hover:border-amber-500/60 min-h-[180px]"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-11 h-11 rounded-lg bg-[#1A1D24] border border-[#2A2F3D] flex items-center justify-center text-amber-500 group-hover:bg-amber-500/10 transition-colors">
                  <Briefcase size={20} />
                </span>
                <span className="sc-mono-eyebrow text-slate-500">Buscar trabajo</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 group-hover:text-amber-500 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Ver ofertas
              </h2>
              <p className="text-sm text-slate-400 leading-relaxed">Ofertas activas, sustituciones y refuerzos filtrados por especialidad.</p>
              <span className="mt-auto pt-4 text-xs font-mono uppercase tracking-widest text-amber-500 group-hover:translate-x-1 transition-transform">
                {JOBS.length}+ ofertas · Entrar →
              </span>
            </Link>

            <Link
              to="/publicar-oferta"
              data-testid="cta-publish-job"
              className="group relative overflow-hidden p-6 md:p-8 flex flex-col rounded-[0.85rem] min-h-[180px] transition-colors bg-amber-500 hover:bg-amber-400 text-slate-950"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-11 h-11 rounded-lg bg-slate-950/10 border border-slate-950/25 flex items-center justify-center text-slate-950">
                  <PlusCircle size={20} />
                </span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-950/70">Para empresas y profesionales</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Publicar oferta
              </h2>
              <p className="text-sm text-slate-950/85 leading-relaxed">Rellena el formulario y llega a la comunidad técnica en minutos.</p>
              <span className="mt-auto pt-4 text-xs font-mono uppercase tracking-widest text-slate-950 group-hover:translate-x-1 transition-transform">
                Gratis para empezar →
              </span>
            </Link>
          </div>

          <div className="sc-mono-eyebrow text-slate-500 mb-3">Búsqueda rápida</div>
          <HeroSearch />
          <div className="flex flex-wrap gap-2 mt-5 items-center">
            <Link to="/profesionales" data-testid="quick-pros" className="text-sm text-slate-300 hover:text-amber-500 border border-[#2A2F3D] hover:border-amber-500/40 px-3 py-1.5 rounded-full transition-colors">Directorio profesionales</Link>
            <Link to="/empresas" data-testid="quick-companies" className="text-sm text-slate-300 hover:text-amber-500 border border-[#2A2F3D] hover:border-amber-500/40 px-3 py-1.5 rounded-full transition-colors">Empresas</Link>
            <Link to="/foro" data-testid="quick-forum" className="text-sm text-slate-300 hover:text-amber-500 border border-[#2A2F3D] hover:border-amber-500/40 px-3 py-1.5 rounded-full transition-colors">Foro & manuales</Link>
          </div>
        </div>
      </section>

      <CategoryGrid />
      <DonationHomeCTA />

      {/* Latest jobs */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="[ 02 ] Ofertas"
          title={t('section.latestJobs')}
          subtitle={t('section.latestJobs.sub')}
          action={t('section.viewAll')}
          actionTo="/ofertas"
          testId="jobs-header"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {sortJobs(JOBS).slice(0, 8).map((j) => <JobCard key={j.id} job={j} compact />)}
        </div>
      </section>

      {/* Professionals */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="[ 03 ] Directorio"
          title={t('section.pros')}
          subtitle={t('section.pros.sub')}
          action={t('section.pros.cta')}
          actionTo="/profesionales"
          testId="pros-header"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROFESSIONALS.slice(0, 6).map((p) => <ProfessionalCard key={p.id} pro={p} />)}
        </div>
      </section>

      {/* Companies */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="[ 04 ] Empresas"
          title={t('section.companies')}
          subtitle={t('section.companies.sub')}
          action={t('section.companies.cta')}
          actionTo="/empresas"
          testId="companies-header"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {COMPANIES.slice(0, 6).map((c) => <CompanyCard key={c.id} company={c} />)}
        </div>
      </section>

      {/* Unified editorial selection */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="home-content-hub">
        <SectionHeader eyebrow="[ 05 ] Comunidad" title={t('content.hubTitle')} subtitle={t('content.hubSubtitle')} action={t('content.viewAll')} actionTo="/actualidad" testId="content-hub-home-header" />
        <div className="flex flex-wrap gap-3 mb-7">{[['news', 'content.news'], ['training', 'content.training'], ['events', 'content.events']].map(([kind, key]) => <Link data-testid={`home-content-${kind}`} key={kind} to={`/actualidad?tipo=${kind}`} className="text-xs font-mono px-4 py-2 rounded-full border border-[#2A2F3D] text-slate-300 hover:border-amber-500/50 hover:text-amber-500 transition-colors">{t(key)}</Link>)}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {NEWS[0] && <NewsCard item={NEWS[0]} />}
          {TRAINING[0] && <TrainingCard item={TRAINING[0]} />}
          {EVENTS[0] && <EventCard item={EVENTS[0]} />}
        </div>
      </section>

      <EmployerCTA />

      <Footer />
    </div>
  );
}
